import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import {
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";
import { OMIGButton } from "../omig/OMIGScreenWrapper";

const ConfirmationStatusScreen = ({ userData, onContinue, onUpdateInfo }) => {
  const { getIdToken } = useAuth();
  const [status, setStatus] = useState(null); // 'confirmed', 'not_found', 'pending'
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkConfirmationStatus();
  }, []);

  useEffect(() => {
    if (status === "not_submitted") {
      onUpdateInfo();
    }
  }, [status]);

  const checkConfirmationStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // Call API to check confirmation status
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/omig/confirmation-status`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to check status: ${response.status}`);
      }

      const data = await response.json();

      // Determine status based on API response
      // Adjust these conditions based on your actual API response structure
      if (data.not_submitted === true) {
        setStatus("not_submitted");
      } else if (data.confirmed === true) {
        setStatus("confirmed");
      } else if (data.no_match === false) {
        setStatus("no_match");
      } else if (data.no_change_detected === true) {
        setStatus("no_change_detected");
      } else {
        setStatus("in_progress");
      }
    } catch (err) {
      console.error("Error checking confirmation status:", err);
      setError(err.message);
      // Default to not_found on error to allow user to proceed
      setStatus("not_submitted");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToOwnli = () => {
    window.open("https://www.ownli.app", "_blank");
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <Card>
          <CardContent className="pt-6 pb-8 flex flex-col items-center justify-center min-h-[300px]">
            <div className="w-12 h-12 border-4 border-t-transparent border-blue-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">
              Checking your confirmation status...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="space-y-6 p-4">
        <Card className="border-red-100 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">
                  Error checking status
                </p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
                <OMIGButton
                  onClick={checkConfirmationStatus}
                  className="mt-4 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </OMIGButton>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (status === "not_submitted") {
    return null;
  }
  // Status: Confirmed
  if (status === "confirmed") {
    return (
      <div className="space-y-6 p-4">
        {/* Success Header */}
        <div className="text-center space-y-4">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Congratulations!</h1>
          <p className="text-gray-600">Your change has been confirmed.</p>
        </div>

        {/* Happy Person Image */}
        <div className="flex justify-center">
          <img
            src="https://parked.blob.core.windows.net/ownli/app-resources%5Comig-female.png"
            alt="Happy person"
            className="w-64 h-64 rounded-lg object-cover"
          />
        </div>

        {/* Reward Card */}
        <Card className="bg-green-50 border-green-100">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <p className="text-gray-900 font-medium">
                To collect your reward, please visit:
              </p>
              <button
                onClick={handleGoToOwnli}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Ownli
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Login Hint */}
        <p className="text-sm text-gray-500 text-center px-4">
          Please use the same phone number or email address to log in to
          Ownli.app
        </p>
      </div>
    );
  }

  // Status: Not Found
  if (status === "no_match") {
    return (
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="w-20 h-20  rounded-full flex items-center justify-center mx-auto">
          <AlertCircle className="w-10 h-10 text-red-500" />
        </div>
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">
            We Couldn't Find a Match
          </h1>
          <p className="text-gray-600">
            We couldn't find your information in our system. Please update your
            contact details and try again.
          </p>
        </div>

        {/* Illustration */}
        <div className="flex justify-center">
          <img
            src="https://parked.blob.core.windows.net/ownli/app-resources%5Cno_match.png"
            alt="Searching"
            className="w-64 h-64 rounded-lg object-cover"
          />
        </div>

        {/* Action Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-gray-700">
                Don't worry! You can update your information and submit again.
              </p>
              <OMIGButton
                onClick={onUpdateInfo}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Contact Information
                <ArrowRight className="w-4 h-4" />
              </OMIGButton>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Status: User Found But No Change Detected
  if (status === "no_change_detected") {
    return (
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Change Not Detected
          </h1>
          <p className="text-gray-600">
            You told us that you made the change, but we couldn't confirm it in
            our records.
          </p>
        </div>

        {/* Illustration */}
        <div className="flex justify-center">
          <img
            src="https://parked.blob.core.windows.net/ownli/app-resources%5Cno_change.png"
            alt="No change detected"
            className="w-64 h-64 rounded-lg object-cover"
          />
        </div>

        {/* Info Card */}
        <Card className="bg-yellow-50 border-yellow-100">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-gray-700 font-medium">
                We found your account, but the change you mentioned hasn't been
                reflected yet.
              </p>
              <p className="text-sm text-gray-600">This could happen if:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>The change was very recent and hasn't processed yet</li>
                <li>The change was made with different information</li>
                <li>Your contact information needs to be updated</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          <OMIGButton
            onClick={onUpdateInfo}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Update My Information
            <ArrowRight className="w-4 h-4" />
          </OMIGButton>
        </div>
      </div>
    );
  }

  // Status: In Progress
  if (status === "in_progress") {
    return (
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <Clock className="w-10 h-10 text-purple-500 animate-pulse" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Hang Tight!</h1>
          <p className="text-gray-600">
            We are in the process of confirming your information.
          </p>
        </div>

        {/* Progress Illustration */}
        <div className="flex justify-center">
          <img
            src="https://parked.blob.core.windows.net/ownli/app-resources%5Cwait_for_confirmation.png"
            alt="Processing"
            className="w-64 h-64 rounded-lg object-cover"
          />
        </div>

        {/* Info Card */}
        <Card className="bg-purple-50 border-purple-100">
          <CardContent className="pt-6">
            <div className="space-y-4 text-center">
              <p className="text-gray-700 font-medium">
                We'll let you know once your information is confirmed.
              </p>
              <p className="text-sm text-gray-600">
                You'll receive a notification when the confirmation is complete.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};
export default ConfirmationStatusScreen;
